import { ServerResponse, WithAuditDetails } from 'data/types';
import { RootState } from 'data/root.reducers';
import { Action, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { History } from 'history';
import { showError, showSuccessMessage } from 'shared/toaster';
import { extractErrorMessage } from 'utils';
import i18n from 'i18n';
import { AxiosError } from 'axios';
import Http from '../../shared/http';
import { MISCELLANEOUS, CONTACT, SYSTEM_CONFIGURATION_ROUTE } from '../../constants';
import { Contact } from './contact.types';

const http = Http.getHttp();

// action export constants
export const API_PATH_CONTACTS = '/contact'
export const GET_ALL_CONTACTS = 'GET_ALL_CONTACTS';
export const GET_CONTACT_BY_ID = 'GET_CONTACT_BY_ID';
export const UPDATE_CONTACT = 'UPDATE_CONTACT';
export const PATCH_CONTACT = 'PATCH_CONTACT';
export const DELETE_CONTACT = 'DELETE_CONTACT';
export const CREATE_CONTACT = 'CREATE_CONTACT';
export const GET_ALL_CONTACTS_FULFILLED = 'GET_ALL_CONTACTS_FULFILLED';
export const GET_CONTACT_BY_ID_FULFILLED = 'GET_CONTACT_BY_ID_FULFILLED';
export const UPDATE_CONTACT_FULFILLED = 'UPDATE_CONTACT_FULFILLED';
export const PATCH_CONTACT_FULFILLED = 'PATCH_CONTACT_FULFILLED';
export const DELETE_CONTACT_FULFILLED = 'DELETE_CONTACT_FULFILLED';
export const CREATE_CONTACT_FULFILLED = 'CREATE_CONTACT_FULFILLED';
export const CREATE_CONTACT_REJECTED = 'CREATE_CONTACT_REJECTED';
export const CONTACTS_CLEAR_ERRORS = 'CONTACTS_CLEAR_ERRORS';
export const OPEN_CONTACT_CREATE_POPUP = 'OPEN_CONTACT_CREATE_POPUP';
export const CLOSE_CONTACT_CREATE_POPUP = 'CLOSE_CONTACT_CREATE_POPUP';
export const SET_CONTACT_LOADING = 'SET_CONTACT_LOADING';
export const GO_TO_CONTACT_LIST = 'GO_TO_CONTACT_LIST';
export const GO_TO_CONTACT_CREATE = 'GO_TO_CONTACT_CREATE';
export const GO_TO_CONTACT_EDIT = 'GO_TO_CONTACT_EDIT';
export const GO_TO_CONTACT_VIEW = 'GO_TO_CONTACT_VIEW';
export const SELECT_CONTACT = 'SELECT_CONTACT';

// action creators
export const getAllContacts = (dfId: string) => ({ type: GET_ALL_CONTACTS, payload: http.get(`/contacts/${dfId}`) });
export const patchContact = (dfId: string, id: number, body: any) => ({ type: PATCH_CONTACT, payload: http.patch(`/contacts/${dfId}/${id}`, body) });

export function deleteContact(
    dfId: string,
    id: string,
) {
    return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
        try {
            await dispatch({ type: DELETE_CONTACT, payload: http.delete(`/contacts/${dfId}/${id}`) });
            showSuccessMessage(i18n.t('contact.delete.success'), 5000);

            dispatch(getAllContacts(dfId));

        } catch (e) {
            const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
            showError(i18n.t('contact.delete.failure', { error: errorMessage }));
        }
    };
}

export function getContactById(
    deliveryFunctionId: string,
    id: string,
    showLoading = true
) {
    return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
        dispatch(setNetworkLoadingAction(showLoading));
        try {
            await dispatch({
                type: GET_CONTACT_BY_ID,
                payload: http.get(`/contacts/${deliveryFunctionId}/${id}`),
            });

        } catch (e) {
            const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
        }
    };
}

export function createContact(
    deliveryFunctionId: string,
    body: WithAuditDetails<Contact>,
    history: History,
) {
    return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
        dispatch(setNetworkLoadingAction(true));
        try {
            await dispatch({
                type: CREATE_CONTACT,
                payload: http.post(`/contacts/${deliveryFunctionId}`, body),
            });
            showSuccessMessage(i18n.t('contact.create.success', { name: body.data?.name }), 5000);
            history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CONTACT}`);

        } catch (e) {
            const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
            showError(i18n.t('contact.create.failed', { name: body.data?.name, error: errorMessage }));
        } finally {
            dispatch(setNetworkLoadingAction(false));
            dispatch(getAllContacts(deliveryFunctionId));
        }
    };
}

export function updateContact(
    deliveryFunctionId: string,
    contactId: string,
    body: WithAuditDetails<Contact>,
    history: History,
) {
    return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
        dispatch(setNetworkLoadingAction(true));
        try {
            await dispatch({
                type: UPDATE_CONTACT,
                payload: http.put(`/contacts/${deliveryFunctionId}/${contactId}`, body),
            });
            showSuccessMessage(i18n.t('contact.edit.success', { name: body.data?.name }), 5000);
            history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CONTACT}`);

        } catch (e) {
            const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
            showError(i18n.t('contact.edit.failed', { name: body.data?.name, error: errorMessage }));
        } finally {
            dispatch(setNetworkLoadingAction(false));
            dispatch(getAllContacts(deliveryFunctionId));
        }
    };
}

export const clearErrors = () => ({ type: CONTACTS_CLEAR_ERRORS, payload: null });

export const openContactCreatePopup = (contact?: Contact) => ({ type: OPEN_CONTACT_CREATE_POPUP, payload: { contact } });

export const closeContactCreatePopup = () => ({ type: CLOSE_CONTACT_CREATE_POPUP });

export const setNetworkLoadingAction = (isLoading: boolean) => ({ type: SET_CONTACT_LOADING, payload: { isLoading } });

export function createContactFromForm(
    deliveryFunctionId: string,
    body: WithAuditDetails<Contact>,
) {
    return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
        dispatch(setNetworkLoadingAction(true));
        try {
            await dispatch({
                type: CREATE_CONTACT,
                payload: http.post(`/contacts/${deliveryFunctionId}`, body),
            });
            dispatch(closeContactCreatePopup());
            showSuccessMessage(i18n.t('contact.create.success', { name: body.data?.name }), 5000);

        } catch (e) {
            const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
            showError(i18n.t('contact.create.failed', { name: body.data?.name, error: errorMessage }));
        } finally {
            dispatch(setNetworkLoadingAction(false));
            dispatch(getAllContacts(deliveryFunctionId));
        }
    };
}

export function goToContactList(history: History): (dispatch: Dispatch) => void {
    return (dispatch: Dispatch) => {
        dispatch({
            type: GO_TO_CONTACT_LIST,
        });

        dispatch({
            type: CLOSE_CONTACT_CREATE_POPUP,
        });

        history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CONTACT}`);
    };
}

export function goToContactCreate(history: History): (dispatch: Dispatch) => void {
    return (dispatch: Dispatch) => {
        dispatch({
            type: GO_TO_CONTACT_CREATE,
        });

        history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CONTACT}/create`);
    };
}

export function goToContactEdit(history: History): (dispatch: Dispatch) => void {
    return (dispatch: Dispatch) => {
        dispatch({
            type: GO_TO_CONTACT_EDIT,
        });

        history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CONTACT}/edit`);
    };
}

export function goToContactView(history: History): (dispatch: Dispatch) => void {
    return (dispatch: Dispatch) => {
        dispatch({
            type: GO_TO_CONTACT_VIEW,
        });

        history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CONTACT}/view`);
    };
}

export function selectContact(contact:Contact[]) {
    return {
        type: SELECT_CONTACT,
        payload: contact,
    };
}
